import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PRESET_FONTS = [
  { name: 'Unbounded', google: 'Unbounded:wght@300;400;600;800' },
  { name: 'Manrope', google: 'Manrope:wght@300;400;500;700' },
  { name: 'Bebas Neue', google: 'Bebas+Neue' },
  { name: 'Oswald', google: 'Oswald:wght@400;700' },
  { name: 'Montserrat', google: 'Montserrat:wght@400;700;900' },
  { name: 'Poppins', google: 'Poppins:wght@400;600;800' },
  { name: 'Playfair Display', google: 'Playfair+Display:wght@400;700;900' },
  { name: 'Cormorant Garamond', google: 'Cormorant+Garamond:wght@400;700' },
  { name: 'Pacifico', google: 'Pacifico' },
  { name: 'Lobster', google: 'Lobster' },
  { name: 'Dancing Script', google: 'Dancing+Script:wght@400;700' },
  { name: 'Permanent Marker', google: 'Permanent+Marker' },
  { name: 'Anton', google: 'Anton' },
  { name: 'Righteous', google: 'Righteous' },
  { name: 'Space Mono', google: 'Space+Mono:wght@400;700' },
];

const loadedGoogle = new Set();
const loadedCustom = new Set();

export const resolveFileUrl = (url) => (url && url.startsWith('/api') ? `${BACKEND_URL}${url}` : url);

export const loadGoogleFont = (name) => {
  const preset = PRESET_FONTS.find((f) => f.name === name);
  if (!preset || loadedGoogle.has(name)) return;
  loadedGoogle.add(name);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${preset.google}&display=swap`;
  document.head.appendChild(link);
};

export const loadCustomFont = async (font) => {
  if (loadedCustom.has(font.name)) return;
  loadedCustom.add(font.name);
  try {
    const face = new FontFace(font.name, `url(${resolveFileUrl(font.url)})`);
    await face.load();
    document.fonts.add(face);
  } catch (e) {
    loadedCustom.delete(font.name);
    console.error('Font load failed', font.name, e);
  }
};

export const ensureFontsForElements = (elements, customFonts = []) => {
  const families = new Set(elements.filter((e) => e.type === 'text' && e.fontFamily).map((e) => e.fontFamily));
  families.forEach((name) => {
    loadGoogleFont(name);
    const custom = customFonts.find((f) => f.name === name);
    if (custom) loadCustomFont(custom);
  });
};

export const useFonts = () => {
  const [customFonts, setCustomFonts] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/fonts`);
      setCustomFonts(res.data);
      res.data.forEach(loadCustomFont);
    } catch (e) {
      console.error('Failed to load fonts', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const upload = async (file, name) => {
    const token = localStorage.getItem('token');
    const form = new FormData();
    form.append('file', file);
    if (name) form.append('name', name);
    const res = await axios.post(`${API}/admin/fonts`, form, { headers: { Authorization: `Bearer ${token}` } });
    await loadCustomFont(res.data);
    setCustomFonts((prev) => [...prev, res.data]);
    return res.data;
  };

  const remove = async (id) => {
    const token = localStorage.getItem('token');
    await axios.delete(`${API}/admin/fonts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setCustomFonts((prev) => prev.filter((f) => f.id !== id));
  };

  return { customFonts, loading, refresh, upload, remove };
};
